import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, validPrice } from '../../utils/formatting';
import {
    avatarTone, initials, prettifyPerson, priceCut, suggestedVsAi, thumbLabel, unitDisplayName, unitYear,
} from '../../utils/taskBoard';
import useTaskCard, { MISSING_UNIT_TITLE } from '../../hooks/useTaskCard';
import CancelTaskConfirm from '../shared/CancelTaskConfirm';
import DeleteTaskConfirm from '../shared/DeleteTaskConfirm';
import { GAP_CHIP_TONES } from '../ui';
import { unitAgingLabel, unitAgingTone } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { PinIcon } from './icons';

const BTN = 'v2-btn v2-btn--default v2-btn--sm';

/** The new price vs the unit's AI value (T1), with a sentence for screen readers. */
export function VsAiChip({ view }) {
    return (
        <span className="v2-gapchip" data-state={view.state} title={view.label}>
            <span className={`v2-chip v2-chip--${GAP_CHIP_TONES[view.state]}`} aria-hidden="true">{view.text}</span>
            <span className="v2-sr">{view.label}</span>
        </span>
    );
}
VsAiChip.propTypes = { view: PropTypes.object.isRequired };

/** The person a task is assigned to, as a coloured initials disc. */
export function Avatar({ email, name, size }) {
    if (!email) {
        return <span className={`tb2-av tb2-av--none${size ? ` tb2-av--${size}` : ''}`} role="img" title="Unassigned" aria-label="Unassigned">+</span>;
    }
    const label = `Assigned to ${name} (${email})`;
    return (
        <span className={`tb2-av tb2-av--t${avatarTone(email)}${size ? ` tb2-av--${size}` : ''}`} role="img" title={label} aria-label={label}>
            {initials(name)}
        </span>
    );
}
Avatar.propTypes = { email: PropTypes.string, name: PropTypes.string, size: PropTypes.string };

/**
 * One task on the redesigned board. The behaviour is hooks/useTaskCard
 * (shared with today's card); a plain click opens the preview (D10) through
 * `onActivate`, a double-click and Enter open the unit (`onOpenUnit`).
 */
function TaskCardV2({
    task, unit, unitStatus, assigneeName, canEdit, draggable, selected, hintId,
    onStatusChange, onQuickStatus, onNavigate, onOpenUnit, onActivate, onDelete, onEdit,
    dragging, onDragStart, onDragEnd,
}) {
    const card = useTaskCard({
        task, unitStatus, canEdit, onStatusChange, onQuickStatus, onNavigate, onOpenUnit, onActivate, onDelete, onEdit,
    });
    const {
        menuOpen, confirming, editing, editTitle, editDetails, editDueDate,
        setEditTitle, setEditDetails, setEditDueDate, moreBtnRef, menuRef, titleInputRef, menuId,
        typeLabel, type, open, due, canOpen, active, quick,
    } = card;
    const stop = (e) => e.stopPropagation();
    const { aging, gapThresholds } = useDealershipSettings();
    const ageLabel = unit ? unitAgingLabel(unit, aging) : '';

    const assignee = task.assigned_to;
    const shownName = assignee ? (assigneeName || prettifyPerson(assignee)) : '';
    const year = unit ? unitYear(unit) : null;
    const unitName = unit ? unitDisplayName(unit) : '';
    const cut = task.type === 'price_change' ? priceCut(task) : null;
    const aiValue = unit ? validPrice(unit.PredictedPrice) : null;
    const vsAi = suggestedVsAi(task, unit, gapThresholds);
    const daysInStock = unit && unit.DaysInStock != null ? Number(unit.DaysInStock) : null;
    const canDrag = Boolean(draggable) && !editing;

    const classes = [
        'tb2-card',
        due?.late ? 'is-late' : '',
        open ? '' : 'is-closed',
        active ? 'is-active' : '',
        editing ? 'is-editing' : '',
        dragging ? 'is-dragging' : '',
        selected ? 'is-selected' : '',
        canOpen ? '' : 'is-no-unit',
        canDrag ? 'is-draggable' : '',
    ].filter(Boolean).join(' ');

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <article
            className={classes}
            tabIndex={0}
            aria-label={`${typeLabel}: ${task.title}`}
            title={canOpen ? undefined : MISSING_UNIT_TITLE}
            data-task-id={task.id}
            aria-describedby={hintId}
            aria-keyshortcuts={canEdit ? 'ArrowUp ArrowDown ArrowLeft ArrowRight Enter S C' : 'ArrowUp ArrowDown ArrowLeft ArrowRight Enter'}
            draggable={canDrag}
            onDragStart={draggable ? (e) => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; onDragStart?.(task); } : undefined}
            onDragEnd={draggable ? () => onDragEnd?.() : undefined}
            onClick={card.handleCardClick}
            onDoubleClick={card.handleCardDoubleClick}
            onKeyDown={card.handleCardKeyDown}
        >
            <div className="tb2-top tb2-fade">
                <span className={`tb2-type tb2-type--${type}`}>{typeLabel}</span>
                <span className="tb2-stock">#{task.stock_number}</span>
            </div>

            {canEdit && !editing && (
                // mousedown keeps focus where it is, so clicking inside the
                // overlay never blurs (and closes) the open menu first.
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                    className={`tb2-quick${menuOpen || confirming ? ' is-open' : ''}`}
                    onClick={stop}
                    onMouseDown={(e) => e.preventDefault()}
                    onDoubleClick={stop}
                    onBlur={card.handleQuickBlur}
                >
                    {open ? (
                        <>
                            {task.status === 'open' && (
                                <button type="button" className="tb2-qb" onClick={quick('in_progress')}>Start</button>
                            )}
                            <button type="button" className="tb2-qb tb2-qb--go" onClick={quick('completed')}>Complete</button>
                        </>
                    ) : (
                        <button type="button" className="tb2-qb" aria-label="Reopen task" onClick={quick('open')}>Reopen</button>
                    )}
                    <button
                        type="button"
                        ref={moreBtnRef}
                        className="tb2-qb tb2-qb--more"
                        aria-label="More actions"
                        aria-haspopup="true"
                        aria-expanded={menuOpen}
                        aria-controls={menuOpen ? menuId : undefined}
                        onClick={card.toggleMenu}
                    >
                        <span aria-hidden="true">⋯</span>
                    </button>
                    {menuOpen && (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <div className="tb2-menu" id={menuId} ref={menuRef} data-task-menu="true" onKeyDown={card.handleMenuKeyDown}>
                            <button type="button" onClick={card.chooseEdit}>Edit</button>
                            <span className="tb2-menu-sep" role="separator" />
                            {open && (
                                <button type="button" className="is-danger" onClick={card.chooseCancel}>Cancel task</button>
                            )}
                            <button type="button" className="is-danger" onClick={card.chooseDelete}>Delete</button>
                        </div>
                    )}
                </div>
            )}

            {unit && (
                <div className="tb2-unit tb2-fade">
                    {/* The inventory payload carries no photo URL (photos load
                        per unit), so the category code stands in. */}
                    <span className="tb2-thumb" aria-hidden="true">{thumbLabel(unit)}</span>
                    <span className="tb2-umeta">
                        <span className="tb2-uline" title={`${unitName}${year ? ` · ${year}` : ''}`}>
                            {unitName}
                            {year && <span>{` · ${year}`}</span>}
                        </span>
                        {unit.BranchName && <span className="tb2-ubranch"><PinIcon />{unit.BranchName}</span>}
                    </span>
                </div>
            )}
            {unitStatus === 'missing' && <span className="tb2-missing tb2-fade">Not in current inventory</span>}

            {editing ? (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div className="tb2-edit" onClick={stop} onKeyDown={stop} onDoubleClick={stop}>
                    <label>
                        <span>Title</span>
                        <input ref={titleInputRef} type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={800} />
                    </label>
                    <label>
                        <span>Due date</span>
                        <input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} />
                    </label>
                    <label>
                        <span>Details</span>
                        <textarea value={editDetails} onChange={(e) => setEditDetails(e.target.value)} rows={3} />
                    </label>
                    <div className="tb2-edit-acts">
                        <button type="button" className={BTN} onClick={card.handleDiscardEdit}>Discard changes</button>
                        <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={card.handleSaveEdit} disabled={!editTitle.trim()}>Save changes</button>
                    </div>
                </div>
            ) : (
                <p className="tb2-title tb2-fade">{task.title}</p>
            )}

            {task.type === 'price_change' && task.suggested_price != null && (
                <div className="tb2-pchange tb2-fade">
                    {cut && <s>{formatCurrency(cut.from)}</s>}
                    <span className="tb2-arr" aria-hidden="true">→</span>
                    <span className="tb2-to">{formatCurrency(task.suggested_price)}</span>
                    {cut && cut.change !== 0 && (
                        <span className={`tb2-delta${cut.change > 0 ? ' is-up' : ''}`}>{cut.label}</span>
                    )}
                    {aiValue != null && (
                        <span className="tb2-aiv">
                            AI value <b>{formatCurrency(aiValue)}</b>
                            {vsAi && <VsAiChip view={vsAi} />}
                        </span>
                    )}
                </div>
            )}

            <div className="tb2-meta tb2-fade">
                <Avatar email={assignee} name={shownName} />
                {due && <span className={`tb2-due tb2-due--${due.tone}`}>{due.text}</span>}
                {daysInStock != null && Number.isFinite(daysInStock) && (
                    <span className="tb2-instock" title={ageLabel ? `Days in stock: ${ageLabel}` : 'Days in stock'}>
                        <i className={`tb2-age v2-tone-${unitAgingTone(unit, aging)}`} aria-hidden="true" />
                        {`${daysInStock.toLocaleString('en-US')}d in stock`}
                    </span>
                )}
            </div>

            {confirming === 'cancel' && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div className="tb2-confirm" onClick={stop} onKeyDown={stop} onDoubleClick={stop}>
                    <CancelTaskConfirm classPrefix="tb2" buttonClass={BTN} dangerClass="tb2-danger" onKeep={card.keepTask} onConfirm={card.confirmCancel} />
                </div>
            )}
            {confirming === 'delete' && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div className="tb2-confirm" onClick={stop} onKeyDown={stop} onDoubleClick={stop}>
                    <DeleteTaskConfirm classPrefix="tb2" buttonClass={BTN} dangerClass="tb2-danger" onKeep={card.keepTask} onConfirm={card.confirmDelete} />
                </div>
            )}
        </article>
    );
}

TaskCardV2.propTypes = {
    task: PropTypes.object.isRequired,
    unit: PropTypes.object,
    unitStatus: PropTypes.oneOf(['found', 'missing', 'unknown']).isRequired,
    assigneeName: PropTypes.string,
    canEdit: PropTypes.bool.isRequired,
    draggable: PropTypes.bool,
    selected: PropTypes.bool,
    hintId: PropTypes.string,
    onStatusChange: PropTypes.func.isRequired,
    onQuickStatus: PropTypes.func.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onOpenUnit: PropTypes.func.isRequired,
    onActivate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    dragging: PropTypes.bool,
    onDragStart: PropTypes.func,
    onDragEnd: PropTypes.func,
};

export default TaskCardV2;
